import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { trpc } from '@/lib/trpc';
import {
  Factory,
  CheckCircle2,
  Clock,
  Package,
  Truck,
  FileSpreadsheet,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatNumber } from '@/lib/utils';
import { ListPageShell } from '@/components/shared/ListPageShell';
import { CompactKpiRibbon, type CompactKpiItem } from '@/components/shared/CompactKpiRibbon';
import { useHeaderSearch } from '@/hooks/useHeaderSearch';
import { PageFilterTags } from '@/components/shared/PageFilterTags';
import { DataTableRow, DataTableRowCheckbox, DataTableSelectAllCheckbox } from '@/components/shared/DataTableRow';
import { useRowSelection } from '@/hooks/useRowSelection';
import { BulkActionBar } from '@/components/shared/BulkActionBar';
import { exportToExcel, type ExcelColumnDef } from '@/lib/excel.utils';

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: 'poId', label: 'Mã PO', width: 16 },
  { key: 'productName', label: 'Sản phẩm', width: 32 },
  { key: 'vendorName', label: 'Nhà gia công', width: 24 },
  { key: 'customerName', label: 'Khách hàng', width: 24 },
  { key: 'targetSent', label: 'SL đi GC', width: 14, format: 'number' },
  { key: 'qtyReceived', label: 'SL hàng về', width: 14, format: 'number' },
  { key: 'exportedQty', label: 'Đã xuất', width: 14, format: 'number' },
  { key: 'remainingQty', label: 'Còn lại', width: 14, format: 'number' },
  { key: 'statusLabel', label: 'Trạng thái', width: 14 },
];
import { OutsourceDispatchModal, OutsourceReceiptModal } from './components';
import { emsTabClass } from "@/components/ui/ems-tabs";
import { usePageSizeState } from "@/hooks/usePreferredPageSize";

export function OutsourceTrackingHubPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { headerSearch } = useHeaderSearch();
  const [selectedVendor, setSelectedVendor] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = usePageSizeState(50);

  // Modals state
  const [selectedPOForDispatch, setSelectedPOForDispatch] = useState<any | null>(null);
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false);
  const [selectedPOForReceipt, setSelectedPOForReceipt] = useState<any | null>(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [headerSearch, selectedVendor, statusFilter]);

  // 1. Fetch Outsource Tracking Data
  const { data, isLoading, refetch } = useQuery(
    trpc.outsourcePos.getTrackingHub.queryOptions({
      search: headerSearch || undefined,
      vendorId: selectedVendor === 'ALL' ? undefined : selectedVendor,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      page,
      limit: pageSize,
    })
  );

  const activeVendors = data?.activeVendors || [];
  const items = data?.items || [];
  const summary = data?.summary;

  const kpiItems = useMemo<CompactKpiItem[]>(() => [
    {
      key: 'ALL',
      label: 'Tổng đơn gia công',
      count: formatNumber(summary?.totalOrders || 0),
      icon: Factory,
      variant: 'blue',
      tooltip: `Tổng số lượng gửi đi: ${formatNumber(summary?.totalSent || 0)} pcs`,
      clickable: true,
      onClick: () => { setStatusFilter('ALL'); setPage(1); },
    },
    {
      key: 'IN_PROGRESS',
      label: 'Đang gia công',
      count: formatNumber(summary?.inProgressCount || 0),
      icon: Clock,
      variant: 'amber',
      tooltip: 'Chờ đối tác trả hàng về xưởng',
      clickable: true,
      onClick: () => { setStatusFilter((prev) => (prev === 'IN_PROGRESS' ? 'ALL' : 'IN_PROGRESS')); setPage(1); },
    },
    {
      key: 'COMPLETED',
      label: 'Đã nhận đủ hàng',
      count: formatNumber(summary?.completedCount || 0),
      icon: CheckCircle2,
      variant: 'emerald',
      tooltip: `Tổng đã về: ${formatNumber(summary?.totalReceived || 0)} pcs`,
      clickable: true,
      onClick: () => { setStatusFilter((prev) => (prev === 'COMPLETED' ? 'ALL' : 'COMPLETED')); setPage(1); },
    },
    {
      key: 'REMAINING',
      label: 'Tồn BTP còn lại',
      count: `${formatNumber(summary?.totalRemaining || 0)} pcs`,
      icon: Package,
      variant: 'indigo',
      tooltip: 'Sẵn sàng đóng gói & giao khách',
    },
  ], [summary]);

  const rowIds = useMemo(() => items.map((po: any) => po.id), [items]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;

  const exportRows = async (rows: any[]) => {
    const data = rows.map((po: any) => ({
      poId: po.poId,
      productName: po.productName,
      vendorName: po.vendor?.shortName || po.vendor?.name || '',
      customerName: po.customer?.shortName || po.customer?.name || '',
      targetSent: po.qtySentTotal || (po.qtyOrdered + po.allowanceQty),
      qtyReceived: po.qtyReceived || 0,
      exportedQty: po.exportedQty || 0,
      remainingQty: po.remainingQty || 0,
      statusLabel: po.status === 'COMPLETED' ? 'Đủ hàng' : 'Đang xử lý',
    }));
    await exportToExcel(data, EXPORT_COLUMNS, 'theo_doi_gia_cong');
  };

  const vendorTags = useMemo(
    () => activeVendors.map((v: any) => ({ key: v.id, label: v.shortName || v.name || v.code, tone: 'purple' as const })),
    [activeVendors],
  );

  const handleOpenDispatchModal = (po: any) => {
    setSelectedPOForDispatch(po);
    setIsDispatchModalOpen(true);
  };

  const handleOpenReceiptModal = (po: any) => {
    setSelectedPOForReceipt(po);
    setIsReceiptModalOpen(true);
  };

  return (
    <>
      <ListPageShell
        unitLabel="đơn gia công"
        totalItems={data?.total || 0}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(1);
        }}
        isLoading={isLoading}
        topBanner={
          <CompactKpiRibbon
            items={kpiItems}
            activeKey={statusFilter}
            onSelect={(key) => {
              setStatusFilter((prev) => (prev === key ? 'ALL' : (key || 'ALL')));
              setPage(1);
            }}
            isLoading={isLoading}
          />
        }
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              className={emsTabClass(true, { size: "sm" })}
            >
              <Factory className="w-3.5 h-3.5 text-indigo-600" />
              <span>Theo Dõi Gia Công</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-primary/10 text-primary tabular-nums">
                {data?.total || 0}
              </span>
            </button>
          </div>
        }
        headerFilters={
          <PageFilterTags
            items={vendorTags}
            selectedKeys={selectedVendor === 'ALL' ? [] : [selectedVendor]}
            onSelect={(key) => { setSelectedVendor(key || 'ALL'); setPage(1); }}
            dropdownLabel="Đối tác"
          />
        }
        actions={
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportRows(items)}
              className="gap-1.5 text-xs h-8"
            >
              <Download className="h-3.5 w-3.5" />
              Xuất Excel
            </Button>
            <Button
              size="sm"
              onClick={() => navigate({ to: '/outsource-orders' })}
              className="gap-1.5 text-xs h-8 bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              DS Đơn GC
            </Button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse apv-compact-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 font-medium">
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                <th className="py-2.5 px-3 w-28">Mã PO / Đơn</th>
                <th className="py-2.5 px-3 min-w-[200px]">Sản Phẩm &amp; Quy Cách</th>
                <th className="py-2.5 px-3 w-32">Nhà Gia Công</th>
                <th className="py-2.5 px-3 w-28">Khách Hàng</th>
                {/* Chặng 1: Gửi đi */}
                <th className="py-2.5 px-3 bg-blue-50 text-blue-700 w-32 text-right">
                  Chặng 1: Đi GC<br />
                  <span className="text-[10px] font-normal text-blue-500">(SL + 1% bù)</span>
                </th>
                {/* Chặng 2: Về kho */}
                <th className="py-2.5 px-3 bg-emerald-50 text-emerald-700 w-36 text-right">
                  Chặng 2: Hàng Về<br />
                  <span className="text-[10px] font-normal text-emerald-500">(Đã về / Lệch)</span>
                </th>
                {/* Chặng 3: Đóng gói & Xuất */}
                <th className="py-2.5 px-3 bg-indigo-50 text-indigo-700 w-36 text-right">
                  Chặng 3: Xuất &amp; Tồn<br />
                  <span className="text-[10px] font-normal text-indigo-500">(Đã xuất / Còn lại)</span>
                </th>
                <th className="py-2.5 px-3 text-center w-24">Trạng Thái</th>
                <th className="py-2.5 px-3 text-center w-28">Thao Tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-gray-400">
                    Đang tải dữ liệu tiến độ gia công...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-gray-400">
                    Không tìm thấy dữ liệu gia công ngoài phù hợp
                  </td>
                </tr>
              ) : (
                items.map((po: any) => {
                  const targetSent = po.qtySentTotal || (po.qtyOrdered + po.allowanceQty);
                  const diff = (po.qtyReceived || 0) - targetSent;
                  const isDone = po.status === 'COMPLETED';

                  return (
                    <DataTableRow
                      key={po.id}
                      id={po.id}
                      rowSelection={rowSelection}
                      className="hover:bg-gray-50/80 transition-colors"
                      onClick={() => navigate({ to: '/outsource-orders/$orderId', params: { orderId: po.id } as any })}
                    >
                      <DataTableRowCheckbox id={po.id} rowSelection={rowSelection} />
                      <td className="py-2.5 px-3 font-mono font-medium text-gray-900">
                        <div>{po.poId}</div>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="font-medium text-gray-900 line-clamp-1">{po.productName}</div>
                        <div className="text-[11px] text-gray-400 font-mono line-clamp-1">{po.productSpecs || po.category}</div>
                      </td>
                      <td className="py-2.5 px-3 font-medium text-gray-700">
                        {po.vendor?.shortName || po.vendor?.name || '---'}
                      </td>
                      <td className="py-2.5 px-3 text-gray-600">
                        {po.customer?.shortName || po.customer?.name || '---'}
                      </td>
                      {/* Chặng 1 */}
                      <td className="py-2.5 px-3 bg-blue-50/30 text-right">
                        <div className="font-mono font-bold text-gray-900">{formatNumber(targetSent)}</div>
                        <div className="text-[10px] text-blue-600">
                          {formatNumber(po.qtyOrdered)} + {formatNumber(po.allowanceQty)} hao hụt
                        </div>
                      </td>
                      {/* Chặng 2 */}
                      <td className="py-2.5 px-3 bg-emerald-50/30 text-right">
                        <div className="font-mono font-bold text-emerald-700">{formatNumber(po.qtyReceived || 0)}</div>
                        <div className="text-[10px] text-slate-500">
                          Đạt: <span className="font-semibold text-emerald-600">{formatNumber(po.qtyAccepted || po.qtyReceived || 0)}</span>
                          {Number(po.qtyRejected || 0) > 0 && (
                            <span className="ml-1 text-rose-600 font-semibold">| Lỗi: {formatNumber(po.qtyRejected)}</span>
                          )}
                        </div>
                        <div className={`text-[10px] font-mono ${diff >= 0 ? 'text-emerald-600' : 'text-rose-600 font-bold'}`}>
                          {diff >= 0 ? `+${formatNumber(diff)}` : formatNumber(diff)}
                        </div>
                      </td>
                      {/* Chặng 3 */}
                      <td className="py-2.5 px-3 bg-indigo-50/30 text-right">
                        <div className="font-mono font-medium text-gray-700">Xuất: {formatNumber(po.exportedQty || 0)}</div>
                        <div className="font-mono font-bold text-indigo-700">Còn: {formatNumber(po.remainingQty || 0)}</div>
                      </td>
                      {/* Trạng thái */}
                      <td className="py-2.5 px-3 text-center">
                        <Badge
                          variant="outline"
                          className={
                            isDone
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                              : 'bg-amber-50 border-amber-200 text-amber-700'
                          }
                        >
                          {isDone ? 'Đủ hàng' : 'Đang xử lý'}
                        </Badge>
                      </td>
                      {/* Thao tác */}
                      <td className="py-2.5 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1.5">
                          {/* Cùng điều kiện với trang xem đơn: xuất gửi khi đã lưu, chưa xong; nghiệm thu khi chưa đủ hàng */}
                          {(po.status === 'PENDING' || po.status === 'IN_PROGRESS') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenDispatchModal(po)}
                            className="h-7 text-xs px-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                            title="Xuất gửi bán thành phẩm đi gia công ngoài"
                          >
                            <Truck className="h-3 w-3 mr-1" />
                            Xuất BTP
                          </Button>
                          )}
                          {!isDone && po.status !== 'COMPLETED' && po.status !== 'CANCELLED' && po.status !== 'DRAFT' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenReceiptModal(po)}
                            className="h-7 text-xs px-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            title="Nghiệm thu BTP hồi xưởng phân định Đạt / Lỗi KCS"
                          >
                            <Package className="h-3 w-3 mr-1" />
                            Nghiệm Thu
                          </Button>
                          )}
                        </div>
                      </td>
                    </DataTableRow>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={[
          {
            icon: <Download className="h-4 w-4" />,
            label: `Xuất Excel ${selectedCount} dòng`,
            onClick: () => exportRows(items.filter((po: any) => selectedIds.has(po.id))),
          },
        ]}
      />

      {/* Outsource Dispatch Modal */}
      <OutsourceDispatchModal
        isOpen={isDispatchModalOpen}
        onClose={() => {
          setIsDispatchModalOpen(false);
          setSelectedPOForDispatch(null);
        }}
        outsourcePO={selectedPOForDispatch}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: [['outsourcePos']] });
          refetch();
        }}
      />

      {/* Outsource KCS Receipt Modal */}
      <OutsourceReceiptModal
        isOpen={isReceiptModalOpen}
        onClose={() => {
          setIsReceiptModalOpen(false);
          setSelectedPOForReceipt(null);
        }}
        outsourcePO={selectedPOForReceipt}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: [['outsourcePos']] });
          refetch();
        }}
      />
    </>
  );
}

export default OutsourceTrackingHubPage;
